"use client";

import React, { forwardRef } from "react";

type InputFieldProps = {
  label?: string;
  id?: string;
  name: string;
  type?: "text" | "email" | "number" | "password" | "textarea";
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
  error?: string;
  maxLength?: number;
  disabled?: boolean;
};

const InputField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputFieldProps
>(function InputField(
  {
    label,
    id,
    name,
    type = "text",
    value,
    onChange,
    onBlur,
    required = false,
    placeholder = "",
    rows = 1,
    className = "",
    error,
    maxLength,
    disabled = false,
  },
  ref
) {
  const baseClass =
    "border border-[#D9D9D9] bg-[#FFFFFF] rounded-md p-2.5 w-full";
  const errorClass = error ? "border-red-500" : "";
  const disabledClass = disabled
    ? "bg-[#F0F0F0] text-gray-500 cursor-not-allowed"
    : "";

  const inputClass = `${baseClass} ${errorClass} ${disabledClass} ${className}`.trim();

  // Replace the browser's default (English, generic) constraint-validation
  // popups — e.g. "Please fill in this field" — with a localized, field-aware
  // Slovak message. Set only while the field is actually invalid.
  const handleInvalid = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const el = e.currentTarget;
    if (el.validity.valueMissing) {
      el.setCustomValidity(
        label ? `Prosím, vyplňte pole „${label}“.` : "Prosím, vyplňte toto pole.",
      );
    } else if (el.validity.typeMismatch && type === "email") {
      el.setCustomValidity("Zadajte platnú e-mailovú adresu (napr. meno@domena.sk).");
    } else {
      el.setCustomValidity("");
    }
  };

  // Any custom message must be cleared as the user edits, otherwise a non-empty
  // customValidity keeps the field permanently invalid. The browser re-runs its
  // checks (and re-fires onInvalid) on the next submit if it's still wrong.
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.currentTarget.setCustomValidity("");
    onChange(e);
  };

  return (
    <div className='flex flex-col'>
      {label && (
        <label htmlFor={id || name} className='text-[15px] mb-1'>
          {label}
          {required && "*"}
        </label>
      )}
      {type === "textarea" ? (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          id={id || name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          onInvalid={handleInvalid}
          required={required}
          rows={rows}
          className={inputClass}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          id={id || name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          onInvalid={handleInvalid}
          required={required}
          className={inputClass}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
        />
      )}
      {error && (
        <p className='text-red-500 text-sm mt-1'>{error}</p>
      )}
    </div>
  );
});

export default InputField;
